import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Image as ImageIcon, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";

/**
 * Image input that accepts either a remote URL OR a direct file upload.
 *
 * On file upload, hits POST /api/upload/image which returns a base64 `data_url`.
 * That data_url is stored back into the same `value` so the parent form can persist it
 * inline on the resource — no static-files mount required.
 *
 *   value:   current image URL (or data: URL) — controlled by parent
 *   onChange: (next: string) => void
 *   testIdPrefix: namespace for data-testids (e.g., "new-service-image")
 */
export default function ImageUploadField({ value = "", onChange, testIdPrefix = "image" }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);

    const pick = () => fileRef.current?.click();

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // reset so picking the same file again still triggers change
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        // Soft cap on the client too — keeps the upload snappy
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Image is larger than 3 MB. Please compress before uploading.");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onChange(data.data_url);
            toast.success(`Image uploaded (${Math.round(data.bytes / 1024)} KB)`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Upload failed"));
        } finally {
            setUploading(false);
        }
    };

    const isData = typeof value === "string" && value.startsWith("data:");

    return (
        <div className="space-y-2" data-testid={`${testIdPrefix}-field`}>
            <div className="flex items-center gap-2">
                <input
                    placeholder="Image URL (https://…) or use Upload"
                    value={isData ? "" : (value || "")}
                    onChange={(e) => onChange(e.target.value)}
                    className="flex-1 bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none"
                    data-testid={`${testIdPrefix}-url`}
                />
                <button
                    type="button"
                    onClick={pick}
                    disabled={uploading}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#C29A5B]/40 text-[#8F622C] text-[0.65rem] tracking-[0.2em] uppercase hover:bg-[#6B1535] hover:text-[#FFF8F2] transition-colors disabled:opacity-50"
                    data-testid={`${testIdPrefix}-upload-btn`}
                >
                    {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                    {uploading ? "Uploading…" : (isData ? "Replace" : "Upload")}
                </button>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleFile}
                    data-testid={`${testIdPrefix}-file-input`}
                />
            </div>

            {value ? (
                <div className="flex items-center gap-3 p-2 border border-[#6B1535]/15 bg-[#FBF6F0]">
                    <div className="w-14 h-14 overflow-hidden bg-[#FBF6F0] flex-shrink-0">
                        <img src={value} alt="Preview" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-[0.6rem] tracking-[0.2em] uppercase text-[#7A5262]">
                            {isData ? "Uploaded image" : "External URL"}
                        </div>
                        <div className="text-[0.65rem] text-[#8A6574] truncate">
                            {isData ? `${Math.round(value.length / 1024)} KB · stored inline` : value}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => onChange("")}
                        className="text-[#7A5262] hover:text-red-700 p-1"
                        data-testid={`${testIdPrefix}-clear`}
                        aria-label="Remove image"
                    >
                        <X size={14} />
                    </button>
                </div>
            ) : (
                <div className="flex items-center gap-2 text-[0.65rem] text-[#8A6574] italic">
                    <ImageIcon size={11} /> No image yet — paste a URL or click Upload (max 3 MB)
                </div>
            )}
        </div>
    );
}
